/**
 * Reduce a snippet of FI-authored rich text to plain text.
 *
 * For HEADINGS and other places a value is rendered as a text node. A custom
 * field's `fieldLabel` is edited in a rich text control, so real configuration
 * carries markup — the flow using disclosures today stores
 * `<p class="mb-0">Disclosures Packet</p>` as its label. The form itself renders
 * that through `dangerouslySetInnerHTML` and it looks right; anywhere the same
 * value is rendered as text, the tags print verbatim.
 *
 * **Not a sanitizer.** It must never be the defence for anything rendered as
 * HTML — use `sanitizeHtml` for that. This only strips markup that has no
 * business being in a name.
 *
 * Has a twin at
 * `acquire-api/src/api/v1/controllers/submissionController/getDisclosures/toPlainText.ts`,
 * kept separate because acquire-api does not depend on this package. Keep the
 * two in step: they resolve the same disclosure label for two different readers,
 * and a difference between them shows up as one surface naming a disclosure
 * differently from another.
 */
const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
  // Punctuation a rich text editor emits constantly. Without these, the
  // fallback below leaves them as literal "&rsquo;" in a heading.
  "&mdash;": "\u2014",
  "&ndash;": "\u2013",
  "&lsquo;": "\u2018",
  "&rsquo;": "\u2019",
  "&ldquo;": "\u201c",
  "&rdquo;": "\u201d",
  "&hellip;": "\u2026",
};

/** `&#8217;` / `&#x2019;` — decoded arithmetically rather than tabulated. */
const decodeNumericEntity = (entity: string): string | null => {
  const match = /^&#(x)?([0-9a-f]+);$/i.exec(entity);
  if (!match) {
    return null;
  }
  const code = parseInt(match[2], match[1] ? 16 : 10);
  // Reject values outside Unicode, which String.fromCodePoint throws on.
  return Number.isFinite(code) && code > 0 && code <= 0x10ffff
    ? String.fromCodePoint(code)
    : null;
};

export const toPlainText = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  return value
    .replace(/<[^>]*>/g, " ")
    .replace(
      /&[a-z#0-9]+;/gi,
      (entity) =>
        ENTITIES[entity.toLowerCase()] ??
        decodeNumericEntity(entity) ??
        // Unrecognised: left EXACTLY as found. Mapping it to a space silently
        // damaged the label — "Member&#8217;s Agreement" became "Member s
        // Agreement" — and a name that is visibly wrong beats one that is
        // quietly wrong on a compliance record.
        entity
    )
    .replace(/\s+/g, " ")
    .trim();
};

export default toPlainText;
